import { Box, Grid, Typography } from "@mui/material";
import { useContext } from "react";
import { useTranslation } from "react-i18next";
import { useParams } from "react-router-dom";
import { useAppSelector } from "../../app/hooks";
import InfoTooltip from "../../common/components/functional/InfoTooltip/InfoTooltip";
import FormSection from "../../common/components/functional/formSection/FormSection";
import FormTable from "../../common/components/functional/formTable/FormTable";
import ZonedSalaryValue from "../../common/components/functional/ZonedSalaryValue";
import { selectTalentById } from "../talents/talentsSlice";
import { Talent, talentIsDeactivated } from "../talents/talentAPI";
import {
  selectAllEmploymentAttributes,
  selectSupportedZones,
} from "../employment/employmentSlice";
import { AccessRightContext } from "../user/AccessRightProvider";
import { ContentKey } from "./TalentDetails";

type Props = {
  handleOpenEditModal: (content: ContentKey) => void;
};

type EmploymentAttributeRow = {
  workTitleId: string;
  tesId: string;
  totalSalary: string;
  zonedSalary?: { [zone: string]: string };
  originalId: string;
};

export const TalentShiftSalaries = ({ handleOpenEditModal }: Props) => {
  const { t } = useTranslation(["talentProfile", "common"]);
  const { talentId } = useParams();
  const employmentAttributes = useAppSelector((state) =>
    selectAllEmploymentAttributes(state),
  );
  const supportedZones = useAppSelector(selectSupportedZones);
  const translatedWorkTitles = useAppSelector(
    (state) => state.localized.workTitle,
  );
  const talent = useAppSelector(
    (state) => selectTalentById(state, talentId as string) as Talent,
  );

  const { canEditTalentProfile } = useContext(AccessRightContext);

  // transform employment attributes into rows that can handle zones
  const transformedRows: EmploymentAttributeRow[] =
    employmentAttributes?.map(({ id, version, zonedSalary, ...item }) => {
      return {
        workTitleId: item.workTitleId,
        tesId: item.tesId,
        totalSalary: item.totalSalary,
        zonedSalary: zonedSalary,
        originalId: id,
      };
    }) || [];

  const formatSalary = (salary: string) =>
    salary ? `${salary.replace(".", ",")} €/h` : "-";

  const formatTotalSalaries = (
    totalSalary: string,
    tesId: string,
    zonedSalary?: { [zone: string]: string },
  ) => {
    const zones = supportedZones?.[tesId];
    const zoneValues = zonedSalary
      ? Object.fromEntries(
          Object.entries(zonedSalary).map(([zone, value]) => [
            zone,
            formatSalary(value),
          ]),
        )
      : undefined;

    return (
      <ZonedSalaryValue
        defaultValue={formatSalary(totalSalary)}
        zones={zones}
        zoneValues={zoneValues}
      />
    );
  };

  const formattedEmploymentAttributes = transformedRows.map((row) => ({
    values: {
      workTitleId: row.workTitleId,
      tesId: row.tesId,
      totalSalary: formatTotalSalaries(
        row.totalSalary,
        row.tesId,
        row.zonedSalary,
      ),
    },
    id: row.originalId,
    deletable: false,
    editable: true,
  }));

  const shiftSalariesHeaders = [
    { text: t("common:workTitleShort") },
    { text: t("common:tes") },
    { text: t("talentProfile:totalSalary") },
  ];

  const translationKeys = ["workTitle", "tes"];

  const addButtonLabel = t("talentProfile:addSalary");

  const Tooltip = (
    <Box display="flex" alignItems="center" marginLeft="13px">
      <InfoTooltip title={t(`talentProfile:shiftSalaryInfo`)} />
    </Box>
  );

  return (
    <FormSection
      title={t("talentProfile:shiftSalaries")}
      margin
      infoTooltip={Tooltip}
    >
      <Grid item container xs={3} justifyContent="flex-end" alignItems="center">
        <Typography textAlign="end" variant="secondary">
          {t("talentProfile:workTitleSpesicifSalaries")}
        </Typography>
      </Grid>
      <Grid item xs={9}>
        <FormTable
          data={formattedEmploymentAttributes.sort((a, b) =>
            translatedWorkTitles[a.values.workTitleId].localeCompare(
              translatedWorkTitles[b.values.workTitleId],
            ),
          )}
          headers={shiftSalariesHeaders}
          translationKeys={translationKeys}
          handleOpenEditModal={handleOpenEditModal}
          contentKey="shiftSalaries"
          addButtonLabel={addButtonLabel}
          disableEditRow={!canEditTalentProfile || talentIsDeactivated(talent)}
        />
      </Grid>
    </FormSection>
  );
};
